import {formatEuro, indexationLine, indexedPrice, moneyInputValue} from '../../core/billing/billing.core.js';
import fieldsTemplate from './billing-item-fields.template.html';
import formStyles from './billing-item-form.styles.css';

const FIELDS_PLACEHOLDER = '<!--billing-item-fields-->';

/**
 * The one copy of the billing item form's fields and their styles: a component's template holds
 * the `<!--billing-item-fields-->` placeholder where the fields go.
 *
 * @param {string} html the component's template
 * @param {string} css the component's own styles
 * @returns {{html: string, css: string}} what to hand to `loadTemplate`
 */
export function withBillingItemFields(html, css) {
    return {html: html.replace(FIELDS_PLACEHOLDER, fieldsTemplate), css: formStyles + '\n' + css};
}

/** Form control selector by field name; the names are the ones `validateBillingItemForm` reads. */
const FIELDS = {
    label: '#billing-label',
    price: '#billing-price',
    cost: '#billing-cost',
    supplier: '#billing-supplier',
    intervalMonths: '#billing-interval',
    paidUntil: '#billing-paid-until',
    active: '#billing-active',
    notes: '#billing-notes',
};

/** Interval a new item starts with. */
const DEFAULT_INTERVAL_MONTHS = 12;

/**
 * Fills the billing form with `item`, or with the defaults of a new item (yearly, active) when
 * `item` is null. Paid until is locked while the item has invoices, since the backend only lets
 * an invoice move it.
 *
 * @param {HTMLFormElement} form
 * @param {import('../../shell/types/billing.types.js').BillingItemApiResponse | null} item
 */
export function fillBillingForm(form, item) {
    form.querySelector(FIELDS.label).value = item?.label ?? '';
    form.querySelector(FIELDS.price).value = moneyInputValue(item?.price);
    form.querySelector(FIELDS.cost).value = moneyInputValue(item?.cost);
    form.querySelector(FIELDS.supplier).value = item?.supplier ?? '';
    form.querySelector(FIELDS.intervalMonths).value = String(item?.intervalMonths ?? DEFAULT_INTERVAL_MONTHS);
    form.querySelector(FIELDS.active).checked = item?.active ?? true;
    form.querySelector(FIELDS.notes).value = item?.notes ?? '';

    showPaidUntil(form, item);
    showBillingFormErrors(form, {});
    resetIndexation(form);
}

/** Inputs of the indexation calculator; they are not item fields and are never sent. */
const INDEXATION_INPUTS = ['#billing-index-base-price', '#billing-index-base', '#billing-index-new'];

/**
 * The calculator's result for what is typed in it.
 *
 * @param {HTMLFormElement} form
 * @returns {{price: string, line: string} | null} the canonical indexed price and its invoice line,
 *   or null while an input does not parse
 */
function indexation(form) {
    const [basePrice, baseIndex, newIndex] = INDEXATION_INPUTS.map((selector) => form.querySelector(selector).value);
    const price = indexedPrice(basePrice, baseIndex, newIndex);
    return price === null ? null : {price, line: indexationLine(basePrice, baseIndex, newIndex, price)};
}

/** @param {HTMLFormElement} form */
function renderIndexation(form) {
    const result = indexation(form);
    form.querySelector('#billing-index-result').textContent = result ? formatEuro(Number(result.price)) : '-';
    form.querySelector('#billing-index-line').textContent = result?.line ?? '';
    form.querySelector('#btn-billing-index-apply').disabled = !result;
    form.querySelector('#btn-billing-index-copy').disabled = !result;
}

/** Collapses the indexation calculator and empties it, for a form that is opened again. */
function resetIndexation(form) {
    for (const selector of INDEXATION_INPUTS) {
        form.querySelector(selector).value = '';
    }
    form.querySelector('#billing-indexation').open = false;
    renderIndexation(form);
}

/**
 * Wires the stateless indexation calculator of the form: it shows the indexed price and its Dutch
 * invoice line as the inputs are typed, "Use as price" puts the price in the price field and "Copy
 * invoice line" asks for the line to be copied with a `clipboard-copy` event. Enter in a calculator
 * input does not submit the item form.
 *
 * @param {HTMLFormElement} form
 * @param {(name: string, detail: object) => void} emit the component's `emit`
 */
export function connectIndexationCalculator(form, emit) {
    for (const selector of INDEXATION_INPUTS) {
        const input = form.querySelector(selector);
        input.addEventListener('input', () => renderIndexation(form));
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') e.preventDefault();
        });
    }
    form.querySelector('#btn-billing-index-apply').addEventListener('click', () => {
        const price = form.querySelector(FIELDS.price);
        price.value = indexation(form).price.replace('.', ',');
        price.focus();
    });
    form.querySelector('#btn-billing-index-copy').addEventListener('click', () => {
        emit('clipboard-copy', {text: indexation(form).line, label: 'Invoice line'});
    });
}

/**
 * Shows the item's paid-until date and locks it while the item has invoices, and touches no other
 * field, so an invoice recorded or undone while the form is open updates just these.
 *
 * @param {HTMLFormElement} form
 * @param {import('../../shell/types/billing.types.js').BillingItemApiResponse | null} item
 */
export function showPaidUntil(form, item) {
    const locked = Boolean(item?.lastInvoice);
    const control = form.querySelector(FIELDS.paidUntil);
    control.value = item?.paidUntil ?? '';
    control.disabled = locked;
    form.querySelector('#billing-paid-until-hint').hidden = !locked;
}

/**
 * @param {HTMLFormElement} form
 * @returns {{label: string, price: string, cost: string, supplier: string, intervalMonths: string,
 *   paidUntil: string, active: boolean, notes: string}} the raw values as typed
 */
export function readBillingForm(form) {
    const values = {};
    for (const [name, selector] of Object.entries(FIELDS)) {
        const control = form.querySelector(selector);
        values[name] = control.type === 'checkbox' ? control.checked : control.value;
    }
    return values;
}

/**
 * Shows one message under each field named in `errors` and clears the others; the first
 * offending field gets focus.
 *
 * @param {HTMLFormElement} form
 * @param {Record<string, string>} errors message by field name
 */
export function showBillingFormErrors(form, errors) {
    for (const [name, selector] of Object.entries(FIELDS)) {
        const message = errors[name] ?? '';
        const error = form.querySelector(`#billing-error-${name}`);
        error.textContent = message;
        error.hidden = !message;
        form.querySelector(selector).setAttribute('aria-invalid', String(Boolean(message)));
    }
    const first = Object.keys(FIELDS).find((name) => errors[name]);
    if (first) {
        form.querySelector(FIELDS[first]).focus();
    }
}

/** @param {HTMLFormElement} form */
export function billingFormSnapshot(form) {
    return JSON.stringify(readBillingForm(form));
}
